import { useCallback, useEffect, useRef, useState } from "react";

type GeoStatus = "unsupported" | "insecure" | "granted" | "prompt" | "denied" | "idle" | "requesting";

export function useGeolocation(options?: PositionOptions) {
  const [status, setStatus] = useState<GeoStatus>("idle");
  const [location, setLocation] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState<string | null>(null);
  const watcherId = useRef<number | null>(null);
  const permRef = useRef<PermissionStatus | null>(null);

  // 권한 상태 초기화
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setStatus("unsupported");
      setError("이 브라우저는 위치 정보를 지원하지 않습니다.");
      return;
    }
    if (!isSecureContext) {
      setStatus("insecure");
      setError("HTTPS 환경에서만 위치 정보를 사용할 수 있습니다.");
      return;
    }

    // Permissions API 지원 시 권한 조회
    const init = async () => {
      try {
        if ("permissions" in navigator && (navigator as Navigator & { permissions: any }).permissions.query) {
          const perm = await (navigator as Navigator & { permissions: any }).permissions.query({ name: "geolocation" as PermissionName });
          permRef.current = perm;
          setStatus(perm.state as GeoStatus); // granted | prompt | denied

          // 권한 변경 이벤트
          perm.onchange = () => setStatus(perm.state as GeoStatus);
        } else {
          // 일부 iOS 사파리는 Permissions API가 없음 → 알 수 없으니 prompt로 취급
          setStatus("prompt");
        }
      } catch {
        setStatus("prompt");
      }
    };
    void init();

    return () => {
      if (permRef.current) permRef.current.onchange = null;
      if (watcherId.current != null) navigator.geolocation.clearWatch(watcherId.current);
    };
  }, []);

  // 1회 요청 (현재 위치 한 번만). 성공 시 onSuccess, 실패 시 onError 콜백으로 전달한다.
  const requestOnce = useCallback(
    (
      onSuccess?: (pos: GeolocationPosition) => void,
      onError?: (err: GeolocationPositionError) => void,
    ) => {
      if (!("geolocation" in navigator)) return;
      setStatus("requesting");
      setError(null);

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation(pos);
          setStatus("granted"); // 실제로는 granted가 아닐 수도 있으나 성공했으니 사용 가능 상태로
          onSuccess?.(pos);
        },
        (err) => {
          const msg =
            err.code === err.PERMISSION_DENIED
              ? "위치 정보 접근이 거부되었습니다. 브라우저/앱 설정에서 권한을 허용해 주세요."
              : err.code === err.POSITION_UNAVAILABLE
              ? "위치 정보를 사용할 수 없습니다."
              : err.code === err.TIMEOUT
              ? "위치 정보 요청이 시간 초과되었습니다."
              : "알 수 없는 오류가 발생했습니다.";
          setError(msg);
          // 권한 거절이면 상태를 denied로 표시
          if (err.code === err.PERMISSION_DENIED) setStatus("denied");
          else setStatus("prompt");
          onError?.(err);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0, ...options },
      );
    },
    [options],
  );

  // 지속 추적 (watchPosition)
  const startWatch = useCallback(() => {
    if (!("geolocation" in navigator)) return;
    setStatus("requesting");
    setError(null);

    watcherId.current = navigator.geolocation.watchPosition(
      (pos) => {
        setLocation(pos);
        setStatus("granted");
      },
      (err) => {
        const msg =
          err.code === err.PERMISSION_DENIED
            ? "위치 정보 접근이 거부되었습니다. 브라우저/앱 설정에서 권한을 허용해 주세요."
            : err.code === err.POSITION_UNAVAILABLE
            ? "위치 정보를 사용할 수 없습니다."
            : err.code === err.TIMEOUT
            ? "위치 정보 요청이 시간 초과되었습니다."
            : "알 수 없는 오류가 발생했습니다.";
        setError(msg);
        if (err.code === err.PERMISSION_DENIED) setStatus("denied");
        else setStatus("prompt");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0, ...options }
    );
  }, [options]);

  const stopWatch = useCallback(() => {
    if (watcherId.current != null) {
      navigator.geolocation.clearWatch(watcherId.current);
      watcherId.current = null;
    }
  }, []);

  return {
    status,       // "granted" | "prompt" | "denied" | "unsupported" | "insecure" | ...
    location,     // GeolocationPosition | null
    error,        // string | null
    requestOnce,  // 현재 위치 1회 요청
    startWatch,   // 지속 추적 시작
    stopWatch,    // 지속 추적 중지
  };
}